import React, { useEffect, useState, useMemo } from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';

import { useTranslation } from '@common/hooks/translation';

import './style.scss';

function Timer({ onFinish, duration, onChange, value, text, variant = '' }) {
  const t = useTranslation();
  const [_milliseconds, setMilliseconds] = useState(duration * 1000);
  const milliseconds = typeof value !== 'undefined' ? value * 1000 : _milliseconds;

  const [minutes, seconds] = useMemo(() => {
    const remainderDuration = moment.duration(milliseconds);
    const seconds = remainderDuration.seconds() + Math.round(remainderDuration.milliseconds() / 1000)

    return [remainderDuration.minutes(), seconds < 0 ? 0 : seconds];
  }, [milliseconds, setMilliseconds]);

  useEffect(() => {
    const start = Date.now();
    const timer = setTimeout(() => {
      if (milliseconds <= 0) onFinish?.();
      else {
        const diff = Date.now() - start;
        setMilliseconds(milliseconds - diff);
        onChange?.((milliseconds - diff) / 1000);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [milliseconds, setMilliseconds]);

  const currentValue = `${minutes < 10 ? `0${minutes}` : minutes}:${seconds < 10 ? `0${seconds}` : seconds}`;

  // Calculate progress percentage for round variant
  const progressPercentage = duration ? ((duration - (milliseconds / 1000)) / duration) * 100 : 0;
  const strokeDashoffset = 314 - (314 * progressPercentage) / 100; // 314 is circumference (2 * π * 50)
  
  // Calculate total seconds remaining for red color at ≤10 seconds
  const totalSecondsRemaining = Math.floor(milliseconds / 1000);
  const isLowTime = totalSecondsRemaining <= 10;

  if (variant === 'round') {
    return (
      <div className="timer-round">
        <svg className="timer-round-svg" viewBox="0 0 140 140">
          {/* Background dashed circle */}
          <circle
            className="timer-round-bg"
            cx="70"
            cy="70"
            r="50"
          />
          {/* Progress circle */}
          <circle
            className={`timer-round-progress${isLowTime ? ' timer-round-progress-red' : ''}`}
            cx="70"
            cy="70"
            r="50"
            style={{ strokeDashoffset }}
          />
          {/* Dot at the end of progress */}
          <circle
            className={`timer-round-dot${isLowTime ? ' timer-round-dot-red' : ''}`}
            cx="120"
            cy="70"
            r="6"
            style={{
              transform: `rotate(${progressPercentage * 3.6}deg)`,
              transformOrigin: '70px 70px'
            }}
          />
        </svg>
        <div className={`timer-round-text${isLowTime ? ' timer-round-text-red' : ''}`}>
          {text ? t(text, currentValue) : currentValue}
        </div>
      </div>
    );
  }

  return (
    <p className={`${variant} timer-count`}>
      {text ? t(text, currentValue) : currentValue}
    </p>
  );
}

export default Timer;

Timer.propTypes = {
  onFinish: PropTypes.func,
  onChange: PropTypes.func,
  duration: PropTypes.number,
  value: PropTypes.number,
  text: PropTypes.string,
  variant: PropTypes.oneOf(['block', 'round'])
};
